import { useMemo, useState } from "react";

import type {
  RequirementStudioDisplayRequirement,
  RequirementStudioDisplayViewModel
} from "../viewmodel/requirementStudioAdapter";
import { requirementConceptFor } from "../viewmodel/requirementConcepts";
import { projectLabelFromId } from "../app/format";
import {
  FacetFilterBar,
  labelForFacetValue,
  requirementFacetOrder,
  requirementGroupByOptions,
  toneForFacetValue,
  useFacetFilter
} from "../components/facets";
import { cx, ProvenanceAffordance } from "./shared";

const requirementStatusTone = {
  approved: "bg-[#EAF1E6] text-[#4E7644]",
  candidate: "bg-[#F6EEDD] text-[#8A6A2E]",
  rejected: "bg-[#F6E7E4] text-[#BC4A3C]",
  superseded: "bg-[var(--chip)] text-[var(--muted2)]"
} as const;

type RequirementStatus = RequirementStudioDisplayRequirement["status"];

interface RequirementTextGroup {
  id: string;
  label: string;
  text: string;
  items: RequirementStudioDisplayRequirement[];
  confidence: number;
  confidenceLabel: string;
  confidenceTone: RequirementStudioDisplayRequirement["confidenceTone"];
  bestConfidenceLabel: string;
  kindLabels: string[];
  sourceRefCount: number;
  sourceLabel: string;
  sourceTitle: string;
  sourceTone: "available" | "missing";
  priorityLabels: string[];
  applicabilityLabel: string;
  conflictCount: number;
  statusCounts: Array<{
    status: RequirementStatus;
    label: string;
    count: number;
  }>;
}

const requirementStatusOrder = [
  "approved",
  "candidate",
  "rejected",
  "superseded"
] as const satisfies readonly RequirementStatus[];

const slugFor = (value: string): string =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "") || "item";

const requirementDisplayGroupId = (parentGroupId: string, conceptKey: string): string =>
  `${parentGroupId}-${slugFor(conceptKey)}`;

const confidenceToneFor = (
  confidence: number
): RequirementStudioDisplayRequirement["confidenceTone"] => {
  if (confidence >= 0.85) {
    return "high";
  }

  if (confidence >= 0.7) {
    return "medium";
  }

  return "low";
};

const confidenceColour = (
  confidenceTone: RequirementStudioDisplayRequirement["confidenceTone"]
): string => {
  if (confidenceTone === "high") {
    return "#4E7644";
  }

  if (confidenceTone === "medium") {
    return "#8A6A2E";
  }

  return "#BC4A3C";
};

type RequirementFacetKey = "system" | "type" | "priority" | "confidence" | "sourceKind";

const requirementFacetKeySet = new Set<string>([
  "system",
  "type",
  "priority",
  "confidence",
  "sourceKind"
]);

const isRequirementFacetKey = (value: string | null): value is RequirementFacetKey =>
  typeof value === "string" && requirementFacetKeySet.has(value);

const requirementFacetKeys = requirementFacetOrder.filter(
  (key): key is RequirementFacetKey => requirementFacetKeySet.has(key)
);

const requirementFacetLabelByKey = new Map<RequirementFacetKey, string>(
  requirementGroupByOptions.flatMap((option) =>
    isRequirementFacetKey(option.value) ? [[option.value, option.label]] : []
  )
);

const requirementFacetValue = (
  facetKey: RequirementFacetKey,
  requirement: RequirementStudioDisplayRequirement
): string | undefined => {
  if (facetKey === "type") {
    return requirement.facets.requirementType;
  }

  if (facetKey === "confidence") {
    return requirement.facets.confidenceBand;
  }

  return requirement.facets[facetKey];
};

const requirementFacetDefs = requirementFacetKeys.map((key) => ({
  key,
  label: requirementFacetLabelByKey.get(key) ?? labeliseFacetKey(key),
  valueOf: (requirement: RequirementStudioDisplayRequirement) =>
    requirementFacetValue(key, requirement),
  toneOf: (value: string) => toneForFacetValue(key, value)
}));

const requirementGroupOptions = requirementGroupByOptions.filter(
  (option) => option.value === null || isRequirementFacetKey(option.value)
);

function labeliseFacetKey(key: string): string {
  return key
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

const requirementSearchTerms = (
  requirement: RequirementStudioDisplayRequirement
): string[] => [
  requirement.text,
  requirement.kindLabel,
  requirement.statusLabel,
  requirement.sourceLinkLabel,
  ...requirementFacetKeys.map((key) => {
    const value = requirementFacetValue(key, requirement);

    return value ? labelForFacetValue(key, value) : "";
  })
];

export const groupRequirementsByConcept = (
  requirements: readonly RequirementStudioDisplayRequirement[],
  parentGroupId: string,
  conflictRequirementIds: readonly string[] = []
): RequirementTextGroup[] => {
  const byConcept = new Map<
    string,
    {
      label: string;
      items: RequirementStudioDisplayRequirement[];
    }
  >();

  for (const requirement of requirements) {
    const concept = requirementConceptFor({
      type: requirement.kind,
      text: requirement.text
    });
    const current = byConcept.get(concept.key);

    if (current) {
      current.items.push(requirement);
    } else {
      byConcept.set(concept.key, { label: concept.label, items: [requirement] });
    }
  }

  const conflictIdSet = new Set(conflictRequirementIds);

  return [...byConcept.entries()].map(([conceptKey, { label, items }]) => {
    const representative = [...items].sort((left, right) => {
      const lengthDifference = right.text.length - left.text.length;

      return lengthDifference !== 0
        ? lengthDifference
        : right.confidence - left.confidence;
    })[0];
    const text = representative?.text ?? label;
    const confidence =
      items.reduce((sum, requirement) => sum + requirement.confidence, 0) /
      Math.max(items.length, 1);
    const confidencePercent = Math.round(confidence * 100);
    const bestConfidenceRequirement = items.reduce(
      (best, requirement) =>
        requirement.confidence > best.confidence ? requirement : best,
      items[0] ?? {
        confidence: 0,
        confidenceLabel: "0% confidence"
      }
    );
    const sourceRefCount = items.reduce(
      (sum, requirement) => sum + requirement.sourceRefCount,
      0
    );
    const firstSourceLocator = items.flatMap(
      (requirement) => requirement.sourceRefs ?? []
    )[0]?.locator;
    const sourceLabel =
      sourceRefCount > 0
        ? `${sourceRefCount} source${sourceRefCount === 1 ? "" : "s"}`
        : "Sources pending";

    return {
      id: requirementDisplayGroupId(parentGroupId, conceptKey),
      label,
      text,
      items,
      confidence,
      confidenceLabel:
        items.length === 1
          ? (items[0]?.confidenceLabel ?? `${confidencePercent}% confidence`)
          : `${confidencePercent}% average confidence`,
      confidenceTone: confidenceToneFor(confidence),
      bestConfidenceLabel: bestConfidenceRequirement.confidenceLabel,
      kindLabels: [...new Set(items.map((requirement) => requirement.kindLabel))],
      sourceRefCount,
      sourceLabel,
      sourceTitle: [sourceLabel, firstSourceLocator]
        .filter((value): value is string => Boolean(value))
        .join(" · "),
      sourceTone: sourceRefCount > 0 ? "available" : "missing",
      priorityLabels: [
        ...new Set(
          items.flatMap((requirement) => {
            const priority = requirement.facets.priority;

            return priority ? [labelForFacetValue("priority", priority)] : [];
          })
        )
      ],
      applicabilityLabel: items.some((requirement) =>
        requirement.kind.includes("takeoff")
      )
        ? "Apartment types · names not emitted on these rows"
        : "Project-wide or source-defined · type names not emitted",
      conflictCount: items.filter((requirement) => conflictIdSet.has(requirement.id))
        .length,
      statusCounts: requirementStatusOrder
        .map((status) => {
          const statusItems = items.filter(
            (requirement) => requirement.status === status
          );

          return {
            status,
            label: statusItems[0]?.statusLabel ?? status,
            count: statusItems.length
          };
        })
        .filter(({ count }) => count > 0)
    };
  });
};

interface RequirementConceptDisplayGroup {
  key: string;
  label: string;
  items: RequirementTextGroup[];
}

const displayGroupsForConcepts = (
  concepts: readonly RequirementTextGroup[],
  groupBy: RequirementFacetKey | null
): RequirementConceptDisplayGroup[] => {
  if (!groupBy) {
    return [{ key: "all", label: "All concepts", items: [...concepts] }];
  }

  const grouped = new Map<string, RequirementTextGroup[]>();

  for (const concept of concepts) {
    const values = [
      ...new Set(
        concept.items.flatMap((requirement) => {
          const value = requirementFacetValue(groupBy, requirement);

          return value ? [value] : [];
        })
      )
    ];
    const key = values.length === 1 ? (values[0] ?? "unlabelled") : "mixed";
    const current = grouped.get(key) ?? [];

    current.push(concept);
    grouped.set(key, current);
  }

  return [...grouped.entries()].map(([key, items]) => ({
    key,
    label:
      key === "mixed"
        ? `Multiple ${
            requirementFacetLabelByKey.get(groupBy)?.toLowerCase() ?? "facets"
          }`
        : key === "unlabelled"
          ? `No ${requirementFacetLabelByKey.get(groupBy)?.toLowerCase() ?? "facet"}`
          : labelForFacetValue(groupBy, key),
    items
  }));
};

export function RequirementsScreen({
  view,
  onRerunStage
}: {
  view: RequirementStudioDisplayViewModel;
  onRerunStage?: (() => Promise<void>) | undefined;
}) {
  const [search, setSearch] = useState("");
  const [collapsedGroupKeys, setCollapsedGroupKeys] = useState<Set<string>>(
    () => new Set()
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rerunBusy, setRerunBusy] = useState(false);
  const [rerunMessage, setRerunMessage] = useState<string | null>(null);
  const projectLabel = projectLabelFromId(view.projectId);
  const searchedRequirements = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return view.requirements;
    }

    return view.requirements.filter((requirement) =>
      requirementSearchTerms(requirement).join(" ").toLowerCase().includes(query)
    );
  }, [search, view.requirements]);
  const facetFilter = useFacetFilter({
    items: searchedRequirements,
    facetDefs: requirementFacetDefs,
    labelFor: labelForFacetValue,
    initialGroupBy: "system"
  });
  const filteredRequirements = facetFilter.filtered;
  const canonicalRequirements = useMemo(
    () =>
      groupRequirementsByConcept(
        filteredRequirements,
        "requirements",
        view.conflictRequirementIds
      ),
    [filteredRequirements, view.conflictRequirementIds]
  );
  const selectedGroupBy = isRequirementFacetKey(facetFilter.groupBy)
    ? facetFilter.groupBy
    : null;
  const conceptDisplayGroups = useMemo(
    () => displayGroupsForConcepts(canonicalRequirements, selectedGroupBy),
    [canonicalRequirements, selectedGroupBy]
  );
  const allConceptCount = useMemo(
    () => groupRequirementsByConcept(view.requirements, "all").length,
    [view.requirements]
  );
  const hasSearch = search.trim().length > 0;
  const hasFacetSelection = Object.values(facetFilter.selection).some(
    (values) => values.length > 0
  );
  const hasNoResults = filteredRequirements.length === 0;
  const clearSearch = () => setSearch("");
  const clearAllFilters = () => {
    setSearch("");
    facetFilter.clearAll();
  };
  const toggleFacetGroup = (groupKey: string) => {
    setCollapsedGroupKeys((current) => {
      const next = new Set(current);

      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }

      return next;
    });
  };
  const rerunRequirements = async () => {
    if (
      !onRerunStage ||
      !globalThis.confirm("This will re-run Requirements and replace its results")
    ) {
      return;
    }

    setRerunBusy(true);
    setRerunMessage(null);
    try {
      await onRerunStage();
      setRerunMessage("Requirements stage queued from the stored project sources.");
    } catch (error) {
      setRerunMessage(
        error instanceof Error ? error.message : "Requirements could not be re-run."
      );
    } finally {
      setRerunBusy(false);
    }
  };

  return (
    <div className="flex-1 overflow-auto px-8 py-[34px] pb-[76px] xl:px-14">
      <div className="mx-auto max-w-[1040px]">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
              Step 1 · what we know
            </div>
            <h1 className="m-0 mt-1 text-[26px] font-extrabold tracking-[-.03em]">
              Requirements
            </h1>
            <p className="m-0 mt-2 max-w-[640px] text-[13.5px] leading-[1.5] text-[var(--muted)]">
              Canonical requirement concepts from the {projectLabel} project documents.
              Repeated source rows are merged; confidence, status and source-link gaps
              remain visible without inventing evidence.
            </p>
          </div>
          <div className="max-w-[330px] rounded-[11px] border border-[#D7E1EA] bg-[#F6FAFD] px-4 py-3 text-right">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[.09em] text-[#3E6E9A]">
              Review-only after Specify
            </div>
            <p className="m-0 mt-1 text-[12.5px] font-semibold leading-[1.4] text-[var(--muted)]">
              Add or change clauses in the project intake before re-running Specify;
              this screen is the generated requirement record.
            </p>
            {onRerunStage ? (
              <button
                type="button"
                data-testid="rerun-requirements-stage"
                onClick={() => void rerunRequirements()}
                disabled={rerunBusy}
                className="mt-3 rounded-[8px] border border-[#BFD6E5] bg-white px-3 py-2 text-[12px] font-extrabold text-[#315F7E] hover:bg-[#EFF7FC] disabled:cursor-wait disabled:opacity-60"
              >
                {rerunBusy ? "Re-running stage…" : "Re-run stage"}
              </button>
            ) : null}
            {rerunMessage ? (
              <p role="status" className="m-0 mt-2 text-[11px] font-bold text-[var(--muted)]">
                {rerunMessage}
              </p>
            ) : null}
          </div>
        </div>

        <div className="my-4 flex flex-wrap items-center gap-[11px] rounded-[11px] border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5">
          <input
            aria-label="Search requirements"
            data-showme="requirements-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.currentTarget.value)}
            placeholder="Search requirements..."
            className="min-w-[210px] flex-1 rounded-[9px] border border-[var(--line3)] bg-[var(--surface)] px-[13px] py-2 text-[12.5px] font-semibold text-[var(--ink)] outline-none focus:border-[var(--ink)]"
          />
          <span className="text-xs font-semibold text-[var(--faint)]">
            {filteredRequirements.length === view.requirements.length
              ? `${view.requirements.length} source rows → ${canonicalRequirements.length} concepts`
              : `${filteredRequirements.length} of ${view.requirements.length} source rows → ${canonicalRequirements.length} concepts`}
          </span>
        </div>

        <div className="mb-4 grid gap-[9px] md:grid-cols-4">
          {[
            ["Canonical concepts", allConceptCount],
            ["Source rows", view.requirements.length],
            ["Conflicts", view.conflictRequirementIds.length],
            ["Unresolved RFIs", view.unresolvedRfiIds.length]
          ].map(([label, value]) => (
            <div
              key={label}
              className="rounded-[11px] border border-[var(--line)] bg-[var(--surface)] px-4 py-3"
            >
              <div className="text-[21px] font-extrabold tracking-[-.03em]">
                {value}
              </div>
              <div className="mt-0.5 text-[11px] font-bold uppercase tracking-[.04em] text-[var(--faint)]">
                {label}
              </div>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <FacetFilterBar
            facets={facetFilter.facets}
            selection={facetFilter.selection}
            onToggle={facetFilter.toggle}
            onClearAll={facetFilter.clearAll}
            groupBy={facetFilter.groupBy}
            groupByOptions={requirementGroupOptions}
            onGroupByChange={facetFilter.setGroupBy}
          />
        </div>

        <div className="mt-5 flex flex-col gap-5">
          {hasNoResults ? (
            <section
              aria-live="polite"
              className="rounded-[15px] border border-dashed border-[var(--line3)] bg-[var(--surface)] px-6 py-8 text-center"
            >
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-[11px] bg-[var(--chip)] text-[18px] font-extrabold text-[var(--muted)]">
                0
              </div>
              <h2 className="m-0 mt-3 text-[18px] font-extrabold tracking-[-.015em] text-[var(--ink)]">
                No requirements match
              </h2>
              <p className="mx-auto mb-0 mt-2 max-w-[520px] text-[13px] font-semibold leading-[1.5] text-[var(--muted)]">
                Nothing matches the current search or facet filters. Clear the search to
                return to the generated requirement record.
              </p>
              <div className="mt-4 flex flex-wrap justify-center gap-2">
                {hasSearch ? (
                  <button
                    type="button"
                    onClick={clearSearch}
                    className="rounded-[10px] bg-[var(--ink)] px-4 py-2.5 text-[12.5px] font-extrabold text-[var(--onSolid)]"
                  >
                    Clear search
                  </button>
                ) : null}
                {hasFacetSelection ? (
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="rounded-[10px] border border-[var(--line3)] bg-[var(--surface)] px-4 py-2.5 text-[12.5px] font-extrabold text-[var(--muted)] hover:bg-[var(--chip)]"
                  >
                    Clear all filters
                  </button>
                ) : null}
              </div>
            </section>
          ) : (
            conceptDisplayGroups.map((group) => {
              const groupKey = `${facetFilter.groupBy ?? "all"}-${group.key}`;
              const collapsed = collapsedGroupKeys.has(groupKey);

              return (
                <section key={groupKey}>
                  <button
                    type="button"
                    aria-expanded={!collapsed}
                    onClick={() => toggleFacetGroup(groupKey)}
                    className="mb-2.5 ml-0.5 flex w-full cursor-pointer items-center gap-2 rounded-[9px] px-1 py-1 text-left hover:bg-[var(--chip)]"
                  >
                    <span className="min-w-0 flex-1 truncate text-[10.5px] font-bold uppercase tracking-[.12em] text-[var(--faint)]">
                      {group.label}
                    </span>
                    <span className="rounded-md bg-[var(--chip)] px-2 py-0.5 text-[9.5px] font-bold text-[var(--muted2)]">
                      {group.items.length} concept{group.items.length === 1 ? "" : "s"}
                    </span>
                    <span className="flex h-5 w-5 items-center justify-center rounded-md text-[10px] font-bold text-[var(--faint)]">
                      {collapsed ? "▶" : "▼"}
                    </span>
                  </button>
                  {collapsed ? null : (
                    <div className="overflow-hidden rounded-[13px] border border-[var(--line)] bg-[var(--surface)]">
                      {group.items.map((requirementGroup) => {
                        const expanded = expandedId === requirementGroup.id;
                        const colour = confidenceColour(
                          requirementGroup.confidenceTone
                        );
                        const degrees = Math.round(requirementGroup.confidence * 360);

                        return (
                          <article
                            key={requirementGroup.id}
                            className="border-t border-[var(--line2)] first:border-t-0"
                          >
                            <div className="grid min-w-0 gap-3 px-5 py-4 lg:grid-cols-[28px_minmax(0,1fr)_auto] lg:items-start">
                              <span
                                title={`${requirementGroup.confidenceLabel} · best row ${requirementGroup.bestConfidenceLabel}`}
                                className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-full"
                                style={{
                                  background: `conic-gradient(${colour} ${degrees}deg, var(--line3) ${degrees}deg)`
                                }}
                              >
                                <span className="h-[13px] w-[13px] rounded-full bg-[var(--surface)]" />
                              </span>
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h3 className="m-0 text-[15px] font-extrabold tracking-[-.01em] text-[var(--ink)]">
                                    {requirementGroup.label}
                                  </h3>
                                  <span className="flex-none rounded-md bg-[var(--ink)] px-2 py-[3px] text-[10px] font-extrabold uppercase tracking-[.04em] text-[var(--onSolid)]">
                                    {requirementGroup.items.length} source
                                    {requirementGroup.items.length === 1 ? "" : "s"}
                                  </span>
                                  {requirementGroup.conflictCount > 0 ? (
                                    <span className="rounded-md bg-[#F6E7E4] px-2 py-[3px] text-[10px] font-bold uppercase tracking-[.04em] text-[#A63F34]">
                                      {requirementGroup.conflictCount} conflict
                                      {requirementGroup.conflictCount === 1 ? "" : "s"}
                                    </span>
                                  ) : (
                                    <span className="rounded-md bg-[#EAF1E6] px-2 py-[3px] text-[10px] font-bold uppercase tracking-[.04em] text-[#4E7644]">
                                      No conflicts indicated
                                    </span>
                                  )}
                                </div>
                                <p className="m-0 mt-2 break-words text-[13.5px] font-semibold leading-[1.55] text-[var(--ink2)] [overflow-wrap:anywhere]">
                                  {requirementGroup.text}
                                </p>
                                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                                  <span className="rounded-md bg-[var(--chip)] px-2 py-1 text-[10.5px] font-bold text-[var(--muted)]">
                                    Type applicability:{" "}
                                    {requirementGroup.applicabilityLabel}
                                  </span>
                                  {requirementGroup.priorityLabels.map((priority) => (
                                    <span
                                      key={priority}
                                      className="rounded-md bg-[#F6EEDD] px-2 py-1 text-[10.5px] font-bold text-[#785C27]"
                                    >
                                      Priority: {priority}
                                    </span>
                                  ))}
                                  <span
                                    title={requirementGroup.sourceTitle}
                                    className={cx(
                                      "rounded-md px-2 py-1 text-[10.5px] font-bold",
                                      requirementGroup.sourceTone === "available"
                                        ? "bg-[#E2ECF4] text-[#3E6E9A]"
                                        : "bg-[#F6EEDD] text-[#8A6A2E]"
                                    )}
                                  >
                                    {requirementGroup.sourceLabel}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center justify-end gap-1.5">
                                {requirementGroup.statusCounts.map((status) => (
                                  <span
                                    key={status.status}
                                    className={cx(
                                      "rounded-md px-2 py-[3px] text-[10px] font-bold uppercase tracking-[.04em]",
                                      requirementStatusTone[status.status]
                                    )}
                                  >
                                    {status.label} {status.count}
                                  </span>
                                ))}
                                <button
                                  type="button"
                                  aria-expanded={expanded}
                                  aria-label={
                                    expanded
                                      ? "Collapse requirement rows"
                                      : "Expand requirement rows"
                                  }
                                  onClick={() =>
                                    setExpandedId((current) =>
                                      current === requirementGroup.id
                                        ? null
                                        : requirementGroup.id
                                    )
                                  }
                                  className="flex h-[30px] w-[30px] flex-none items-center justify-center rounded-[7px] text-[10px] font-bold text-[var(--faint)] hover:bg-[var(--chip)]"
                                >
                                  {expanded ? "▼" : "▶"}
                                </button>
                              </div>
                            </div>
                            {expanded ? (
                              <div className="border-t border-[var(--line2)] bg-[var(--raised)] px-5 py-4 lg:pl-[53px]">
                                <div className="mb-2 flex flex-wrap items-center gap-2">
                                  <span className="text-[9.5px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
                                    Source excerpts
                                  </span>
                                  <span className="rounded-md bg-[var(--chip)] px-2 py-1 text-[10px] font-bold text-[var(--muted)]">
                                    {requirementGroup.items.length} row
                                    {requirementGroup.items.length === 1 ? "" : "s"}
                                  </span>
                                </div>
                                <div className="grid gap-2">
                                  {requirementGroup.items.map((requirement, index) => (
                                    <div
                                      key={requirement.id}
                                      className="rounded-[11px] border border-[var(--line2)] bg-[var(--raised)] px-3 py-2.5"
                                    >
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-[10px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
                                          Row {index + 1}
                                        </span>
                                        <span
                                          className={cx(
                                            "rounded-md px-2 py-[3px] text-[10px] font-bold uppercase tracking-[.04em]",
                                            requirementStatusTone[requirement.status]
                                          )}
                                        >
                                          {requirement.statusLabel}
                                        </span>
                                        <span className="rounded-md bg-[var(--chip)] px-2 py-[3px] text-[10px] font-bold uppercase tracking-[.04em] text-[var(--muted)]">
                                          {requirement.kindLabel}
                                        </span>
                                        <span className="ml-auto text-[11.5px] font-semibold text-[var(--faint)]">
                                          {requirement.confidenceLabel}
                                        </span>
                                      </div>
                                      <p className="m-0 mt-2 break-words text-[12.5px] font-semibold leading-[1.5] text-[var(--ink2)] [overflow-wrap:anywhere]">
                                        {requirement.text}
                                      </p>
                                      <div className="mt-2">
                                        <ProvenanceAffordance
                                          evidence={{
                                            sourceRefs: requirement.sourceRefs,
                                            sourceRefCount: requirement.sourceRefCount,
                                            ruleTraceRefs: [],
                                            confidence: requirement.confidence,
                                            confidenceLabel: requirement.confidenceLabel
                                          }}
                                        />
                                      </div>
                                      <div className="mt-2 flex flex-wrap items-center gap-2">
                                        <span className="text-[9.5px] font-bold uppercase tracking-[.08em] text-[var(--faint)]">
                                          Source summary
                                        </span>
                                        <span className="rounded-md bg-[var(--chip)] px-2 py-1 text-[11px] font-bold text-[var(--muted)]">
                                          {requirement.sourceSummary}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : null}
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
